import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DataGrid } from "@mui/x-data-grid";
import { getFieldData } from "./FOFOReport";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import FileSaver from "file-saver";
import { AuthState } from "context/AuthContext";
import { roleAccess } from "../EboPaymentWorking";

const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	if (data && typeof data === "object") return [data];
	return [];
};

const formatHeaderName = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();

const formatCellValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

const getPreferredCommissionFields = (firstRow = {}) => {
	const preferredOrder = [
		"franchise_name",
		"store_name",
		"store_code",
		"admsite_code",
		"commission_percent",
		"effective_date",
		"status",
	];
	const availablePreferred = preferredOrder.filter((field) => Object.prototype.hasOwnProperty.call(firstRow, field));
	const extraFields = Object.keys(firstRow).filter((field) => field !== "id" && !preferredOrder.includes(field));
	return [...availablePreferred, ...extraFields];
};

function FOFOCommissionMaster() {
	const [commissionRows, setCommissionRows] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const [uploadedFileName, setUploadedFileName] = useState("");
	const [displayFranchise, setDisplayFranchise] = useState("");
	const [franchise, setFranchiseList] = useState([]);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

    const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	
	const roleAccessData = roleAccess(user);

    const handleGetStoreFranchiseFetch = async () => {
            try {
                setLoadingOpen(true);
                await pythonVmInstance({
                    url: `/ebo-payment-working/master`,
                    headers: {
                        "Content-Type": "application/json",
                    },
                    params: {
                        master_type: "commission",
                    },
                    method: "GET",
                }).then((res) => {
                    setFranchiseList(getFieldData(res?.data));
                });
                setLoadingOpen(false);
            } catch (err) {
                setSnackBarOpen(true);
                setLoadingOpen(false);
                setSnackBar({
                    ...snackBar,
                    message: "Failed to fetch franchise file",
                    severity: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });
            }
        };

    useEffect(() => {
        handleGetStoreFranchiseFetch();
    }, []);

	const handleFileSelect = useCallback((event) => {
		const file = event.target.files?.[0] || null;
		event.target.value = "";

		if (!file) return;
		if (!file.name.toLowerCase().endsWith(".csv")) {
			setSelectedFile(null);
			setUploadedFileName("");
			setError("Please select a CSV file.");
			setSuccess("");
			return;
		}

		setSelectedFile(file);
		setUploadedFileName(file.name);
		setError("");
		setSuccess("");
	}, []);

	const handleFileUpload = async (e) => {
			e.preventDefault();
			setLoadingOpen(true);
			let form = new FormData();
			if (!selectedFile) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Please upload files and try again",
					severity: "warning",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
				return;
			}
	
			form.append("files", selectedFile);
			form.append("email", user.email);
			form.append("store_type", "fofo");

			pythonVmInstance
				.post("/ebo-payment-working/upload-comm", form, { headers: { "Content-Type": "multipart/form-data" } })
				.then((res) => {
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: "Uploaded the files successfully",
						severity: "success",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
	
					setSelectedFile(null);
					setUploadedFileName("");
					handleGetStoreFranchiseFetch();

				})
				.catch((err) => {
					const message = err?.response?.data?.message;
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: message ? message : "Unable to uploaded the files",
						severity: "error",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				});
		};

	const handleDownloadComm = async () => {
		try {
			setLoadingOpen(true);
			const params = new URLSearchParams({
				store_type: "fofo",
			});
			await pythonVmInstance({
				url: `/ebo-payment-working/franchise-comm?${params.toString()}`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "GET",
				responseType: "blob",
			}).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, `fofo_commission_master.csv`);
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setLoadingOpen(false);
			setSnackBar({
				...snackBar,
				message: "Failed to download file",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	const handleGetStores = async () => {
		if (!displayFranchise) return;

		try {
			setLoadingOpen(true);
			const res = await pythonVmInstance({
				url: `/ebo-payment-working/franchise-stores`,
				headers: {
					"Content-Type": "application/json",
				},
				params: {
					store_type: "fofo",
					franchise: displayFranchise,
				},
				method: "GET",
			});

			const rows = getResponseRows(res?.data).map((row, index) => ({
				id: row?.id ?? row?._id ?? `${displayFranchise}-${index + 1}`,
				...row,
			}));

			setCommissionRows(rows);
			setSuccess(rows.length ? "Stores fetched successfully." : "No stores found for selected franchise.");
		} catch (err) {
			setCommissionRows([]);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: err?.response?.data?.message || "Failed to fetch stores",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};


	const tableRows = useMemo(() => commissionRows, [commissionRows]);

	const columns = useMemo(() => {
		const firstRow = tableRows[0];
		if (!firstRow) return [];

		const orderedFields = getPreferredCommissionFields(firstRow);

		return orderedFields.map((field) => {
			const baseColumn = {
				field,
				headerName: formatHeaderName(field),
				sortable: false,
				filterable: false,
				resizable: true,
				headerAlign: "center",
			};

			if (field === "franchise_name") {
				return {
					...baseColumn,
					flex: 1.2,
					minWidth: 180,
					align: "left",
					renderCell: (params) => (
						<Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 600,
									color: "#111827",
									width: "100%",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{formatCellValue(params?.value) || "-"}
							</Typography>
						</Box>
					),
				};
			}

			if (field === "store_name") {
				return {
					...baseColumn,
					flex: 1.35,
					minWidth: 210,
					align: "left",
					renderCell: (params) => (
						<Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 500,
									color: "#111827",
									width: "100%",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{formatCellValue(params?.value) || "-"}
							</Typography>
						</Box>
					),
				};
			}

			if (field === "store_code") {
				return {
					...baseColumn,
					flex: 0.8,
					minWidth: 120,
					align: "left",
					renderCell: (params) => (
						<Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 700,
									color: "#2563eb",
									width: "100%",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{formatCellValue(params?.value) || "-"}
							</Typography>
						</Box>
					),
				};
			}

			if (field === "admsite_code") {
				return {
					...baseColumn,
					flex: 0.85,
					minWidth: 120,
					align: "left",
					renderCell: (params) => (
						<Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}>
							<Typography
								variant="body2"
								sx={{
									width: "100%",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{formatCellValue(params?.value) || "-"}
							</Typography>
						</Box>
					),
				};
			}

			if (field === "commission_percent") {
				return {
					...baseColumn,
					flex: 0.8,
					minWidth: 130,
					align: "center",
					renderCell: (params) => (
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 700,
									color: "#0f172a",
									width: "100%",
									textAlign: "center",
								}}>
								{formatCellValue(params?.value) || "-"}
							</Typography>
						</Box>
					),
				};
			}

			if (field === "effective_date") {
				return {
					...baseColumn,
					flex: 0.95,
					minWidth: 150,
					align: "center",
					renderCell: (params) => (
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
							<Typography
								variant="body2"
								sx={{
									width: "100%",
									textAlign: "center",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{formatCellValue(params?.value) || "-"}
							</Typography>
						</Box>
					),
				};
			}

			if (field === "status") {
				return {
					...baseColumn,
					flex: 0.75,
					minWidth: 110,
					align: "center",
					renderCell: (params) => {
						const value = String(params?.value || "").toLowerCase();
						const isActive = value === "active";
						return (
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
								<Box
									sx={{
										px: 1.2,
										py: 0.35,
										borderRadius: 999,
										fontSize: 11,
										fontWeight: 800,
										lineHeight: 1.2,
										textTransform: "capitalize",
										backgroundColor: isActive ? "#ecfdf5" : "#fef3c7",
										color: isActive ? "#166534" : "#92400e",
									}}>
									{formatCellValue(params?.value) || "-"}
								</Box>
							</Box>
						);
					},
				};
			}

			return {
				...baseColumn,
				flex: 1,
				minWidth: 150,
				align: "left",
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}>
						<Typography
							variant="body2"
							sx={{
								width: "100%",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}>
							{formatCellValue(params?.value) || "-"}
						</Typography>
					</Box>
				),
			};
		});
	}, [tableRows]);

	return (
		<Box width="100%" p={1.5} sx={{ background: "linear-gradient(180deg, #fafafa 0%, #ffffff 100%)" }}>
			<Stack spacing={1.5}>
				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Box>
							<Typography variant="subtitle1" fontWeight={700}>
								Upload Commission File
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								Select franchise, choose CSV file, then upload.
							</Typography>
						</Box>

						<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}> 
                                
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<UploadFileIcon />}
                                    size="small"
                                    sx={{ height: 38, px: 2 }}>
                                    Select CSV
                                    <input hidden accept=".csv,text/csv" type="file" onChange={handleFileSelect} />
                                </Button>


                                <Button
                                    variant="contained"
                                    onClick={handleFileUpload}
                                    disabled={!selectedFile || ["VIEWER", "EDITOR"].includes(roleAccessData.roles)} // Disable upload for view-only users
                                    size="small"
                                    sx={{ height: 38, px: 2 }}>
                                    Upload
                                </Button>
                            </Stack>
							<Button
								variant="outlined"
								startIcon={<FileDownloadIcon />}
								onClick={handleDownloadComm}
								size="small"
								sx={{ height: 38, px: 2 }}>
								Stores
							</Button>
						</Stack>

						{uploadedFileName ? (
							<Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
								Selected File: {uploadedFileName}
							</Typography>
						) : null}
					</Stack>
				</Paper>

				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Box>
							<Typography variant="subtitle1" fontWeight={700}>
								Store Wise Commission
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								Select franchise and get stores to view commission in table.
							</Typography>
						</Box>

						<Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
							<TextField
								select
								size="small"
								label="Select Franchise"
								value={displayFranchise}
								onChange={(event) => {
									setDisplayFranchise(event.target.value);
									setCommissionRows([]);
								}}
								sx={{
									minWidth: { xs: "100%", md: 240 },
									"& .MuiInputBase-root": {
										height: 38,
										borderRadius: 1.5,
									},
									"& .MuiInputBase-input": {
										py: 0.85,
										fontSize: 13,
									},
								}}>
								{franchise.length === 0 ? (
									<MenuItem value="" disabled>
										No franchise available
									</MenuItem>
								) : (
									franchise.map((franchise) => (
										<MenuItem key={franchise?.name} value={franchise?.name}>
											{franchise?.name}
										</MenuItem>
									))
								)}
							</TextField>

							<Button
								variant="contained"
								startIcon={<SearchIcon />}
								onClick={handleGetStores}
								disabled={!displayFranchise}
								size="small"
								sx={{ height: 38, px: 2 }}>
								Get Stores
							</Button>
						</Stack>

						{error ? <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert> : null}
						{success ? <Alert severity="success" sx={{ py: 0.5 }}>{success}</Alert> : null}
						<Box sx={{ width: "100%", height: "350px" }}>
							<DataGrid
								rows={tableRows}
								columns={columns}
								disableColumnMenu
								disableRowSelectionOnClick
								disableVirtualization
								density="compact"
								pageSizeOptions={[25, 50, 100]}
								rowHeight={46}
								initialState={{
									pagination: {
										paginationModel: { pageSize: 25, page: 0 },
									},
								}}
								getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "commission-row-even" : "commission-row-odd")}
								sx={{
									border: "1px solid #dbe4f0",
									borderRadius: 3,
									fontSize: 13,
									overflow: "hidden",
									"& .MuiDataGrid-main": {
										overflow: "hidden",
									},
									"& .MuiDataGrid-virtualScroller": {
										overflowX: "hidden",
										overflowY: "scroll",
										scrollbarWidth: "thin",
										msOverflowStyle: "auto",
										WebkitOverflowScrolling: "touch",
									},
									"& .MuiDataGrid-virtualScrollerContent": {
										width: "100%",
									},
									"& .MuiDataGrid-columnHeader": {
										display: "flex",
										alignItems: "center",
										background: "linear-gradient(180deg, #f8fafc 0%, #eef4fb 100%)",
									},
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: "#f8fafc",
										minHeight: 44,
										maxHeight: 44,
										borderBottom: "1px solid #dbe4f0",
										alignItems: "center",
									},
									"& .MuiDataGrid-columnHeaderTitleContainer": {
										alignItems: "center",
									},
									"& .MuiDataGrid-columnHeaderTitle": {
										fontWeight: 800,
										fontSize: 12,
										letterSpacing: 0.45,
										color: "#0f172a",
									},
									"& .MuiDataGrid-row": {
										minHeight: "46px !important",
										maxHeight: "46px !important",
										borderBottom: "1px solid #edf2f7",
									},
									"& .MuiDataGrid-row:hover": {
										backgroundColor: "#f8fbff",
									},
									"& .MuiDataGrid-cell": {
										py: 0,
										px: 1,
										display: "flex",
										alignItems: "center",
										justifyContent: "flex-start",
										whiteSpace: "nowrap",
										overflow: "hidden",
										borderTop: "none",
									},
									"& .MuiDataGrid-cellContent": {
										width: "100%",
										textAlign: "left",
										overflow: "hidden",
										textOverflow: "ellipsis",
										display: "flex",
										alignItems: "center",
										justifyContent: "flex-start",
										height: "100%",
									},
									"& .MuiDataGrid-columnSeparator": {
										color: "#d7dee8",
									},
									"& .commission-row-even": {
										backgroundColor: "#ffffff",
									},
									"& .commission-row-odd": {
										backgroundColor: "#fbfdff",
									},
									"& .MuiDataGrid-footerContainer": {
										borderTop: "1px solid #dbe4f0",
										backgroundColor: "#fcfdff",
									},
								}}
							/>
						</Box>
					</Stack>
				</Paper>
			</Stack>
		</Box>
	);
}

export default FOFOCommissionMaster;
